import { throttle } from "lodash";

import "./safeareas.css";

export interface SafeAreaInsetValues {
	top: number;
	bottom: number;
	left: number;
	right: number;
}

/** Get the computed size of safe areas from the CSS variables set on the document root. */
function getSafeAreaInsetValues(): SafeAreaInsetValues {
	const compStyle = getComputedStyle(document.documentElement);

	return {
		top: parseInt(compStyle.getPropertyValue("--safe-area-top-value"), 10) || 0,
		bottom: parseInt(compStyle.getPropertyValue("--safe-area-bottom-value"), 10) || 0,
		left: parseInt(compStyle.getPropertyValue("--safe-area-left-value"), 10) || 0,
		right: parseInt(compStyle.getPropertyValue("--safe-area-right-value"), 10) || 0,
	};
}

/**
 * The current sizes of the safe area insets. Changes fire "-fellow-safeareachange" events on the window, as well as
 * mutating this object.
 */
export const safeAreaInsets = getSafeAreaInsetValues();

/**
 * An event fired when a change in safe area size is detected
 *
 * @remarks
 * These are fired based on resize events, and automatically throttled to only fire every 200ms.
 */
export class SafeAreaChangeEvent extends Event {
	insets: SafeAreaInsetValues;
	constructor(insets: SafeAreaInsetValues, eventInitDict?: EventInit) {
		super("-fellow-safeareachange", eventInitDict);
		this.insets = insets;
	}
}

// Augmenting the WindowEventMap interface makes `addEventListener` infer the event type correctly
declare global {
	interface WindowEventMap {
		"-fellow-safeareachange": SafeAreaChangeEvent;
	}
}

window.addEventListener(
	"resize",
	throttle(() => {
		const newValues = getSafeAreaInsetValues();
		let changed = false;

		// Object.keys() is just typed to return string[] -- technically correct, still very annoying
		for (const key of Object.keys(newValues) as (keyof typeof newValues)[]) {
			if (key in newValues && key in safeAreaInsets) {
				if (newValues[key] !== safeAreaInsets[key]) {
					safeAreaInsets[key] = newValues[key];
					changed = true;
				}
			}
		}

		if (changed) {
			let changeEvent = new SafeAreaChangeEvent(newValues);
			window.dispatchEvent(changeEvent);
		}
	}, 200),
);
